<template>
  <section
    class="chat-content"
    :class="{ 'plain-content': ['MEETING', 'AUDIENCE'].includes(typeCall) }"
    ref="refChatRoom"
  >
    <ContainerChatAutoScroll has-button-scroll ref="refAutoScroll">
      <div
        :key="'isActiveMyLanguageOnly' + userStore.userPreferences.isActiveMyLanguageOnly"
        :class="domainClasses.container"
      >
        <!--이전대화보기-->
        <ButtonMessageHistory v-if="isUsingDirectMessage" />
        <!--메시지-->
        <template
          v-for="(message, messageIndex) in messageList"
          :key="message.callLogId + userStore.userPreferences.isActiveMyLanguageOnly"
        >
          <component
            v-if="isVisibleComponent(messageIndex)"
            :is="getMessageComponent(message, messageComponentMap)"
            :id="'content-' + messageIndex"
            :ref="(el) => message.callLogId === lastCallLogId && setRefMessageLast(el)"
            :messages="messageList"
            :message="message"
            :last-call-log-id="lastCallLogId"
            :loading="loading"
            @playAudioManual="playAudioManual"
          />
        </template>
        <GeneralMessageNotice v-if="isVisibleMicNotice" type="error" :message="t('alert_mic_off')" />
        <GeneralMessageCredit :domain="domain" />
      </div>
      <!--버튼-->
      <template #buttons>
        <ButtonDirectMessage v-if="isVisibleDMButton" @scroll-bottom="scrollToBottom" />
      </template>
    </ContainerChatAutoScroll>
  </section>
  <AudioPlayer />
</template>

<script setup lang="ts">
import { type Component, computed, inject, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Capacitor } from '@capacitor/core';
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';

import GeneralMessageCredit from '@/components/domains/chat/chat-general/GeneralMessageCredit.vue';
import GeneralMessageNotice from '@/components/domains/chat/chat-general/GeneralMessageNotice.vue';
import AudioPlayer from '@/components/domains/chat/common/AudioPlayer.vue';
import ButtonDirectMessage from '@/components/domains/chat/common/ButtonDirectMessage.vue';
import ButtonMessageHistory from '@/components/domains/chat/common/ButtonMessageHistory.vue';
import ContainerChatAutoScroll from '@/components/shared/composite/ContainerChatAutoScroll.vue';
import { useButtonToggleMic } from '@/composables/domains/chat/useButtonToggleMic';
import { useMessagesCall } from '@/composables/domains/chat/useMessagesCall';
import useSize from '@/composables/shared/useSize';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useMessageStore } from '@/stores/message';
import { useMicrophoneStore } from '@/stores/microphone';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { TypeCall, TypeDomain } from '@/types/common';
import { ContainerChatAutoScrollExposed, MessageComponentKey } from '@/types/components';
import { ChatMessage } from '@/types/socket';
import getMessageComponent from '@/utils/chat/getMessageComponent';
import {
  AUDIENCE_MESSAGE_COMPONENT_MAP,
  CONFERENCE_MESSAGE_GUEST_COMPONENT_MAP,
  CONFERENCE_MESSAGE_HOST_COMPONENT_MAP,
  GENERAL_MESSAGE_COMPONENT_MAP,
  MEETING_MESSAGE_COMPONENT_MAP,
} from '@/utils/constants/chat';
import {
  ij_bottomBarStatus,
  ij_popBubbleEmpty,
  ij_setEmptyBubbleMy,
  ij_setEmptyBubbleOther,
} from '@/utils/injectionKeys.js';

const MAX_VISIBLE_NUM: Readonly<number> = 80 as const;

const callStore = useCallStore();
const userStore = useUserStore();
const toastStore = useToastStore();
const audioStore = useAudioStore();
const microphoneStore = useMicrophoneStore();
const messageStore = useMessageStore();

const { t } = useI18n();
const { isMobileSize } = useSize('refChatRoom');
const { messageList, popBubbleEmpty, setEmptyBubbleMy, setEmptyBubbleOther, resetUseMessagesCall } = useMessagesCall();
const { isVisibleMic } = useButtonToggleMic();

const bottomBarStatus = inject(ij_bottomBarStatus, ref(0));

const refChatRoom = ref(); // 대화 구역의 ref
const refMessageLast = ref<HTMLElement>(); // 마지막 메세지의 ref
const loading = ref(false);
const showTutorial = ref(false);
const hasReceivedMessage = ref<boolean>(false);
const refAutoScroll = ref<ContainerChatAutoScrollExposed>(); // 자동스크롤 컨테이너

const { languageOptions } = storeToRefs(audioStore);

const props = withDefaults(
  defineProps<{
    domain?: TypeDomain;
    typeCall?: TypeCall;
  }>(),
  {
    domain: 'CHAT',
    typeCall: 'GENERAL',
  }
);

const messageComponentMap = computed<Record<MessageComponentKey, Component>>(() => {
  const isGuest = QRCodeType.isGuest(callStore.qrCodeInfo.qrCodeType);
  return (
    {
      CONFERENCE: isGuest ? CONFERENCE_MESSAGE_GUEST_COMPONENT_MAP : CONFERENCE_MESSAGE_HOST_COMPONENT_MAP,
      GENERAL: GENERAL_MESSAGE_COMPONENT_MAP,
      AUDIENCE: AUDIENCE_MESSAGE_COMPONENT_MAP,
      MEETING: MEETING_MESSAGE_COMPONENT_MAP,
    }[props.typeCall] ?? GENERAL_MESSAGE_COMPONENT_MAP
  );
});

const isVisibleMicNotice = computed<boolean>(() => {
  return !hasReceivedMessage.value && !messageStore.status.isMicActivated && isVisibleMic.value;
});

const stopWatchReceivedMessage = watch(
  () => messageStore.messages.some(({ category }) => ['MESSAGE', 'DIRECT_MESSAGE'].includes(category)),
  (hasMessage) => {
    if (hasMessage) {
      hasReceivedMessage.value = true;
      stopWatchReceivedMessage();
    }
  }
);

const isUsingDirectMessage = computed<boolean>(() => {
  return callStore.qrCodeInfo.options?.directMessage;
});

const isVisibleDMButton = computed<boolean>(() => {
  return isUsingDirectMessage.value && isMobileSize.value && bottomBarStatus.value === 0;
});

const domainClasses = computed<Record<string, string>>(() => {
  return (
    {
      CHAT: {
        container: 'chat-message-container',
      },
      SELF_SERVICE: {
        container: 'chat-selfservice-message-container',
      },
    }[props.domain] || {
      container: 'chat-message-container',
    }
  );
});

// 마지막 메세지 callLogId
const lastCallLogId = computed(() => {
  return messageList.value[messageList.value.length - 1]?.callLogId;
});

const isVisibleComponent = (inx: number) => {
  const threshold = messageList.value.length - MAX_VISIBLE_NUM;
  return isUsingDirectMessage.value || (inx >= threshold && inx < messageList.value.length);
};

const scrollToBottom = debounce(() => {
  refAutoScroll.value?.scrollToBottom();
}, 300);

const checkAudioPlaySupport = (languageSet): boolean => {
  const languageArray = [...languageSet];

  return languageArray.every((lang) => languageOptions.value[lang].voiceOutputEnabled);
};

const playMeetingAudioManual = (isPlaying: boolean, { callLogId, tgtAudioKeys, tgtLanguageSet }: ChatMessage) => {
  if (microphoneStore.isMicOn) {
    toastStore.warn(t('sound_unavailable_mic_on'));
    return;
  }

  if (!checkAudioPlaySupport(tgtLanguageSet)) {
    toastStore.error(t('not_support_audio_play'));
    return;
  }

  if (isPlaying) {
    audioStore.setPlayState(0, callLogId);
    return;
  } else if (tgtAudioKeys && tgtAudioKeys.length > 0) {
    audioStore.setAudioManulSrcs(true, callLogId, tgtAudioKeys);
  }
};

const playAudienceAudioManual = async (
  isPlaying: boolean,
  { callLogId, tgtAudioKeys, tgtLanguageSet }: ChatMessage
) => {
  if (!checkAudioPlaySupport(tgtLanguageSet)) {
    toastStore.error(t('not_support_audio_play'));
    return;
  }

  if (isPlaying) {
    audioStore.setPlayState(0, callLogId);
  } else if (tgtAudioKeys && tgtAudioKeys.length > 0) {
    if (microphoneStore.isMicOn) {
      // 수동재생 중일 때 마이크 일시중지
      await microphoneStore.stopMic();
      const watcherAudioEnd = watch(
        () => audioStore.playState,
        async (newVal) => {
          if (newVal === 0 && !microphoneStore.isMicOn && bottomBarStatus.value === 0) {
            await microphoneStore.startMic(microphoneStore.selectedMicrophone.id);
            watcherAudioEnd();
          }
        }
      );
      audioStore.controllVolume();
      console.warn('playVolume', audioStore.playVolume);
    }
    audioStore.setAudioManulSrcs(true, callLogId, tgtAudioKeys);
  }
};

const playAudioManual = (isPlaying: boolean, message: ChatMessage) => {
  switch (props.typeCall) {
    case 'AUDIENCE':
      playAudienceAudioManual(isPlaying, message);
      break;
    case 'MEETING':
      playMeetingAudioManual(isPlaying, message);
      break;
    default:
      break;
  }
};

// 마지막 메세지에 ref 추가
const setRefMessageLast = (el) => {
  if (!el || !el.$el) return;
  refMessageLast.value = el.$el;
};

provide(ij_popBubbleEmpty, popBubbleEmpty);
provide(ij_setEmptyBubbleMy, setEmptyBubbleMy);
provide(ij_setEmptyBubbleOther, setEmptyBubbleOther);

onBeforeMount(async () => {
  const isAndroid = Capacitor.getPlatform() === 'android';
  if (isAndroid) await microphoneStore.ensurePermissions();
});

onMounted(async () => {
  const tutorialSeen = localStorage.getItem('tutorialSeen');
  if (!tutorialSeen) showTutorial.value = true;

  await audioStore.fetchLanguageOptions();
});

onUnmounted(() => {
  messageStore.resetMessageStore();
  resetUseMessagesCall();
  stopWatchReceivedMessage();
});

watch(
  [() => messageList.value.length, () => refAutoScroll.value?.maxContentHeight],
  ([newValLength, newValHeight], [_, oldValHeight]) => {
    // 메세지 개수 80개 넘어갔을 떄 발생하는 과도한 여백 현상 방지.
    if (newValLength < MAX_VISIBLE_NUM && !isUsingDirectMessage.value) return;
    if (newValHeight === oldValHeight) {
      refAutoScroll.value?.setMaxContentHeight(0);
    }
  }
);

defineExpose({
  scrollToBottom,
});
</script>

<style scoped lang="scss">
@import '@/assets/sass/layout/_selfservice.scss';

::v-deep .fade-enter-from,
::v-deep .fade-leave-to {
  opacity: 0;
}

::v-deep .fade-enter-active,
::v-deep .fade-leave-active {
  transition: opacity 0.5s ease;
}

.chat-content {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  &.plain-content {
    padding: 1rem 0;
  }
}

.chat-message-container {
  display: flex;
  width: 100%;
  max-width: 75rem;
  flex-direction: column;
  flex-grow: 1;
  margin: 0 auto;
}

.scroll-down {
  position: fixed;
  bottom: 6rem;
  right: 1.25rem;
  z-index: $z-floating;
  box-shadow: $shadow-button;
}
</style>
